import React, { useMemo } from 'react'
import { useGetHrefContext } from '@scentregroup/shared/context'
import { completelyCanonicalUrl } from '@scentregroup/shared/helpers/canonical-link'
import { getDefaultCentreMetadata } from '@scentregroup/shared/helpers/get-default-centre-metadata'
import {
  CentreStaticData,
  fetchCentreStaticData,
} from '@scentregroup/shared/hub-components/universal-header'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import Layout from '@scentregroup/shared/hub-components/layout'
import { CoreContainer } from '@hub/core-container'
import { BrowseStores } from '@scentregroup/shared/components/browse-stores'
import {
  getCentreDetails,
  CentreDetails,
  buildContactLinks,
} from '@scentregroup/shared/helpers/centre'
import Head from 'next/head'
import { useStoredCentre } from '@scentregroup/shared/hooks/use-stored-centre'
import { SGLinkedData } from '@scentregroup/shared/hub-components/json-ld'
import { sortBy } from 'lodash'
import { useRouter } from 'next/router'
import { stringToSlug } from '@scentregroup/shared/helpers/slug'
import {
  GetServerSidePropsResult,
  GetServerSidePropsContext,
} from '../../../types/next'
import { makeApolloClient } from '../../../lib/apolloClient'
import { OpenGraph } from '../../../lib/open-graph'
import {
  Category,
  StoreDetails,
} from '@scentregroup/shared/types/browse-stores'
import { getStoresForCentre } from '../../../lib/get-stores-for-centre'
import {
  getTopStripProps,
  ITopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'
import { ParsedUrlQuery } from 'querystring'
import { ItemList, Thing, WithContext } from 'schema-dts'
import { BreadcrumbContainer } from '../../../components/breadcrumb-container'
import Breadcrumbs, {
  IBreadcrumb,
} from '@scentregroup/shared/hub-components/breadcrumbs'
import { getEnvBool } from '@scentregroup/shared/config'
import {
  MaybeRedirect,
  shouldRedirectForMismatchedRoute,
} from '@scentregroup/shared/lib/redirects'

const getRetailCategories = (
  stores: StoreDetails[]
): (Category & { slug: string })[] => {
  const storeCategories = stores.flatMap(store =>
    store.categories.map(c => ({
      ...c,
      slug: stringToSlug(c.title),
    }))
  )
  const filteredCategories = storeCategories.filter(
    (category, index, array) =>
      array.findIndex(
        c => c.id === category.id && c.title === category.title
      ) === index
  )

  return sortBy(filteredCategories, [o => o.title.toLowerCase()])
}

function shouldRedirectForCategoriesInQueryParams(
  query: ParsedUrlQuery
): MaybeRedirect {
  const { centre, category, subCategory } = query

  if (!category) {
    return undefined
  }

  const categoryPath = [
    Array.isArray(category) ? category[0] : category,
    Array.isArray(subCategory) ? subCategory[0] : subCategory,
  ]
    .filter((el): el is string => Boolean(el))
    .map(el => stringToSlug(el))
    .join('/')

  return {
    redirect: {
      destination: `/${centre}/browse-stores${
        categoryPath ? `/${categoryPath}` : ''
      }`,
      permanent: true,
    },
  }
}

export async function getServerSideProps(
  ctx: GetServerSidePropsContext<{
    centre: string
  }>
): Promise<GetServerSidePropsResult<IBrowseStoresPageProps>> {
  setCacheHeaders(ctx.res)
  const country = localeToCountry(ctx.locale)
  const centreSlug = ctx.params?.centre

  // N.B. this is a server-side only feature flag so we get it directly
  // from process.env, MKP-7744:
  const isNationalStoreDirectoryEnabled = getEnvBool(
    process.env.FEAT_FLAG_ENABLE_STORE_DIRECTORY
  )
  if (!centreSlug) {
    return {
      notFound: true,
    }
  }

  const redirectForCategoriesInQueryParams =
    shouldRedirectForCategoriesInQueryParams(ctx.query)

  if (redirectForCategoriesInQueryParams) {
    return redirectForCategoriesInQueryParams
  }

  const apollo = makeApolloClient()
  const centre = await getCentreDetails(apollo, { slug: centreSlug, country })
  if (!centre) {
    return {
      notFound: true,
    }
  }
  const redirectForCentreSlug = shouldRedirectForMismatchedRoute(
    '/[centre]/browse-stores/[[...categories]]',
    ctx.params,
    {
      centre: centre.slug,
    }
  )
  if (redirectForCentreSlug) {
    return redirectForCentreSlug
  }
  const [stores, headerStaticData, topStripProps] = await Promise.all([
    getStoresForCentre(apollo, centreSlug),
    fetchCentreStaticData(apollo, {
      country,
      centre,
    }),
    getTopStripProps(apollo, country, centreSlug),
  ])

  if (!stores) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      country,
      centre,
      topStripProps,
      stores,
      headerStaticData,
      isNationalStoreDirectoryEnabled,
    },
  }
}

export interface IBrowseStoresPageProps {
  country: ReturnType<typeof localeToCountry>
  headerStaticData: CentreStaticData
  stores: StoreDetails[]
  centre: CentreDetails
  topStripProps: ITopStripProps | null
  isNationalStoreDirectoryEnabled: boolean
}

type MaybeCategory =
  | (Category & {
      slug: string
    })
  | undefined

const useBreadcrumbs = ({
  isNationalStoreDirectoryEnabled,
  centre,
  category,
  subCategory,
}: {
  isNationalStoreDirectoryEnabled: boolean
  centre: CentreDetails
  category: MaybeCategory
  subCategory: MaybeCategory
}): IBreadcrumb[] => {
  return useMemo(() => {
    const breadcrumbs: IBreadcrumb[] = [{ title: 'Home', linkTo: '/' }]
    const browseStores = `/${centre.slug}/browse-stores`
    if (isNationalStoreDirectoryEnabled) {
      breadcrumbs.push({ title: 'All Westfield stores', linkTo: '/stores' })
      breadcrumbs.push({
        title: `${centre.title} stores`,
        linkTo: browseStores,
      })
    } else {
      breadcrumbs.push({
        title: 'Browse stores',
        linkTo: browseStores,
      })
    }
    if (category) {
      breadcrumbs.push({
        title: category.title,
        linkTo: `/${centre.slug}/browse-stores/${category.slug}`,
      })
    }
    if (subCategory) {
      breadcrumbs.push({ title: subCategory.title })
    }
    breadcrumbs[breadcrumbs.length - 1].linkTo = undefined
    return breadcrumbs
  }, [centre, category, subCategory, isNationalStoreDirectoryEnabled])
}

export default function BrowseStoresPage({
  country,
  centre,
  topStripProps,
  stores,
  headerStaticData,
  isNationalStoreDirectoryEnabled,
}: IBrowseStoresPageProps): JSX.Element {
  const canonical = completelyCanonicalUrl(useGetHrefContext()())
  const meta = getDefaultCentreMetadata({
    centre,
    canonical,
  })

  const contactLinks = buildContactLinks(centre)
  useStoredCentre(centre)

  const router = useRouter()

  const query = createBrowseStoresQueryFromParams(router.query)

  const categories = useMemo(() => getRetailCategories(stores), [stores])

  const category = useMemo(
    () => categories.find(c => c.slug === query.category),
    [categories, query.category]
  )
  const subCategory = useMemo(
    () => categories.find(c => c.slug === query.subCategory),
    [categories, query.subCategory]
  )

  const categoryContentSchema =
    subCategory?.seoContentSchema || category?.seoContentSchema

  const contentSchema = {
    ...meta.schema,
    ...(categoryContentSchema || {}),
  }

  const filteredStores: StoreDetails[] = useMemo(() => {
    if (category && subCategory) {
      return stores.filter(
        store => store.categories?.some(c => c.id === subCategory.id)
      )
    }
    if (category) {
      return stores.filter(
        store =>
          store.categories?.some(
            c => c.id === category.id || c.parentId === category.id
          )
      )
    }
    return stores
  }, [category, stores, subCategory])

  const title = useMemo(() => {
    const normalise = (text: string): string =>
      text
        .toLowerCase()
        .replace(/(stores|cafes|bars|pubs|bakeries) stores/, '$1')
    const template = (text: string): string =>
      `Browse ${text} at Westfield ${centre.title}`
    if (category && subCategory) {
      return template(
        subCategory.seoTitle || normalise(`${subCategory.title} stores`)
      )
    }
    if (category) {
      return template(category.seoTitle || category.title)
    }
    return `Browse stores at Westfield ${centre.title}`
  }, [category, centre.title, subCategory])

  const description = useMemo(() => {
    const template = (text: string): string =>
      `Westfield ${centre.title} - ${text}`
    const defaultDescription = `your one-stop hub for shopping, fun & relaxation. Shop our range of fashion, entertainment, dining & wellbeing or check out great events & activities.`
    if (category && subCategory) {
      return template(subCategory.shortDescription || defaultDescription)
    }
    if (category) {
      return template(category.shortDescription || defaultDescription)
    }
    return template(defaultDescription)
  }, [category, centre, subCategory])

  const domain = router.domainLocales?.find(
    l => l.defaultLocale === router.locale
  )?.domain

  const ldItemList = useLDItemList(
    filteredStores,
    centre.slug,
    domain,
    router.asPath
  )

  meta.title = title
  meta.description = description
  const breadcrumbs = useBreadcrumbs({
    isNationalStoreDirectoryEnabled,
    centre,
    category,
    subCategory,
  })
  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" key="description" content={meta.description} />
        <link rel="canonical" href={canonical} />
      </Head>
      <SGLinkedData item={contentSchema as WithContext<Thing>} />
      <SGLinkedData item={ldItemList} />
      <OpenGraph data={meta.openGraphData} />
      <Layout
        country={country}
        centre={{
          ...centre,
          contactLinks,
        }}
        topStrip={topStripProps}
        header={{
          variant: 'centre',
          data: headerStaticData,
          logoText: centre.title,
        }}
      >
        <BreadcrumbContainer>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </BreadcrumbContainer>
        <CoreContainer sx={{ marginBottom: 'spacing-sm' }}>
          <BrowseStores
            stores={filteredStores}
            centre={centre}
            retailCategories={categories}
            query={{
              category,
              subCategory,
              searchTerm: query.searchTerm,
              giftCardAccepted: query.giftCardAccepted,
            }}
          />
        </CoreContainer>
      </Layout>
    </>
  )
}
const createBrowseStoresQueryFromParams = (
  query: ParsedUrlQuery
): {
  category?: string
  subCategory?: string
  searchTerm?: string
  giftCardAccepted?: boolean
} => {
  return {
    ...(Array.isArray(query.categories)
      ? { category: query.categories[0] }
      : {}),
    ...(Array.isArray(query.categories)
      ? { subCategory: query.categories[1] }
      : {}),
    ...(query?.giftCardAccepted
      ? {
          giftCardAccepted: [query.giftCardAccepted].flat().at(-1) === 'true',
        }
      : {}),
    ...(query.searchTerm
      ? { searchTerm: [query.searchTerm].flat().at(-1) }
      : {}),
  }
}

// Creating a Carousel schema which uses ItemList schema
// https://developers.google.com/search/docs/appearance/structured-data/carousel
const useLDItemList = (
  stores: StoreDetails[],
  centre: string,
  domain?: string | null,
  path?: string | null
): WithContext<ItemList> | undefined =>
  React.useMemo<WithContext<ItemList> | undefined>(
    () =>
      // it's invalid to provide google with an item list with only one item
      stores.length > 1
        ? {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            '@id': `${completelyCanonicalUrl(`${domain}${path}`)}#itemlist`,
            itemListElement: stores.map((store, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              url: completelyCanonicalUrl(
                `${domain}/${centre}/store/${store.id}/${store.slug}`
              ),
            })),
          }
        : undefined,
    [stores, centre, domain, path]
  )
